import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    Box,
    Paper,
    Typography,
    TextField,
    Grid,
    Button,
    Divider,
    Snackbar,
    Alert,
    Stack,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/centers";

const Section = ({ title, children }) => (
    <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
        <Typography fontWeight="bold">{title}</Typography>
        <Divider sx={{ my: 2 }} />
        {children}
    </Paper>
);

const FileUpload = ({ label, valueFile, onPickFile, accept }) => (
    <Box>
        <Typography fontSize={13} fontWeight="bold" mb={1}>
            {label}
        </Typography>

        <Button component="label" variant="outlined">
            Choose File
            <input
                hidden
                type="file"
                accept={accept}
                onChange={(e) => onPickFile(e.target.files?.[0] || null)}
            />
        </Button>

        {valueFile && (
            <Typography fontSize={12} mt={1}>
                Selected: <b>{valueFile.name}</b>
            </Typography>
        )}
    </Box>
);

export default function CenterEdit() {
    const { id } = useParams();
    const nav = useNavigate();

    const [loading, setLoading] = useState(true);

    const [values, setValues] = useState({
        name: "",
        code: "",
        phone: "",
        email: "",
        address: "",
        mapLink: "",
        status: "INACTIVE",
        notes: "",

        rentalContract: { startDate: "", endDate: "", file: null },
        fireSafetyContract: { startDate: "", endDate: "", file: null },
        healthLicense: { issueDate: "", expiryDate: "", authority: "", licenseNumber: "", file: null },
    });

    const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });

    const fetchOne = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API_URL}/${id}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
            });
            const c = res.data?.data;

            setValues({
                name: c?.name || "",
                code: c?.code || "",
                phone: c?.phone || "",
                email: c?.email || "",
                address: c?.address || "",
                mapLink: c?.mapLink || "",
                status: c?.status || "INACTIVE",
                notes: c?.notes || "",

                rentalContract: {
                    startDate: c?.rentalContract?.startDate ? String(c.rentalContract.startDate).slice(0, 10) : "",
                    endDate: c?.rentalContract?.endDate ? String(c.rentalContract.endDate).slice(0, 10) : "",
                    file: null,
                },
                fireSafetyContract: {
                    startDate: c?.fireSafetyContract?.startDate ? String(c.fireSafetyContract.startDate).slice(0, 10) : "",
                    endDate: c?.fireSafetyContract?.endDate ? String(c.fireSafetyContract.endDate).slice(0, 10) : "",
                    file: null,
                },
                healthLicense: {
                    issueDate: c?.healthLicense?.issueDate ? String(c.healthLicense.issueDate).slice(0, 10) : "",
                    expiryDate: c?.healthLicense?.expiryDate ? String(c.healthLicense.expiryDate).slice(0, 10) : "",
                    authority: c?.healthLicense?.authority || "",
                    licenseNumber: c?.healthLicense?.licenseNumber || "",
                    file: null,
                },
            });
        } catch (e) {
            setSnack({
                open: true,
                severity: "error",
                message: e?.response?.data?.message || e.message || "Failed to load center",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOne();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const setField = (name, val) => setValues((p) => ({ ...p, [name]: val }));
    const setNested = (block, name, val) =>
        setValues((p) => ({ ...p, [block]: { ...p[block], [name]: val } }));

    const onSave = async () => {
        try {
            const fd = new FormData();

            fd.append("name", values.name);
            fd.append("code", values.code);
            fd.append("phone", values.phone);
            fd.append("mapLink", values.mapLink);
            fd.append("status", values.status);

            if (values.email) fd.append("email", values.email);
            if (values.address) fd.append("address", values.address);
            if (values.notes) fd.append("notes", values.notes);

            // rental
            if (values.rentalContract.startDate) fd.append("rentalContract[startDate]", values.rentalContract.startDate);
            if (values.rentalContract.endDate) fd.append("rentalContract[endDate]", values.rentalContract.endDate);
            if (values.rentalContract.file) fd.append("rentalContract[file]", values.rentalContract.file);

            // fire
            if (values.fireSafetyContract.startDate) fd.append("fireSafetyContract[startDate]", values.fireSafetyContract.startDate);
            if (values.fireSafetyContract.endDate) fd.append("fireSafetyContract[endDate]", values.fireSafetyContract.endDate);
            if (values.fireSafetyContract.file) fd.append("fireSafetyContract[file]", values.fireSafetyContract.file);

            // health license
            if (values.healthLicense.issueDate) fd.append("healthLicense[issueDate]", values.healthLicense.issueDate);
            if (values.healthLicense.expiryDate) fd.append("healthLicense[expiryDate]", values.healthLicense.expiryDate);
            if (values.healthLicense.authority) fd.append("healthLicense[authority]", values.healthLicense.authority);
            if (values.healthLicense.licenseNumber) fd.append("healthLicense[licenseNumber]", values.healthLicense.licenseNumber);
            if (values.healthLicense.file) fd.append("healthLicense[file]", values.healthLicense.file);

            await axios.put(`${API_URL}/${id}`, fd, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                    "Content-Type": "multipart/form-data",
                },
            });

            setSnack({ open: true, severity: "success", message: "Center updated ✅" });
            setTimeout(() => nav(`/centers/${id}`), 700);
        } catch (e) {
            setSnack({
                open: true,
                severity: "error",
                message: e?.response?.data?.message || e.message || "Update failed ❌",
            });
        }
    };

    return (
        <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
            <Box height={80} />
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => nav(-1)}
                    sx={{ borderRadius: 3 }}
                >
                    Back
                </Button>

                <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={onSave}
                    disabled={loading}
                    sx={{ borderRadius: 3 }}
                >
                    Save
                </Button>
            </Stack>

            <Typography variant="h5" fontWeight={900} mb={2}>
                Edit Center
            </Typography>

            {loading ? (
                <Alert severity="info" sx={{ borderRadius: 3 }}>Loading...</Alert>
            ) : (
                <>
                    <Section title="Basic Information">
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <TextField label="Center Name" fullWidth value={values.name} onChange={(e) => setField("name", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label="Center Code" fullWidth value={values.code} onChange={(e) => setField("code", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Typography fontSize={12} mb={0.5}>Phone</Typography>
                                <PhoneInput country="ae" value={values.phone} onChange={(v) => setField("phone", v)} inputStyle={{ width: "100%" }} />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <TextField label="Email" fullWidth value={values.email} onChange={(e) => setField("email", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label="Map Link" fullWidth value={values.mapLink} onChange={(e) => setField("mapLink", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField label="Status" fullWidth value={values.status} onChange={(e) => setField("status", e.target.value)} />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField label="Address" fullWidth value={values.address} onChange={(e) => setField("address", e.target.value)} />
                            </Grid>
                        </Grid>
                    </Section>

                    <Section title="Rental Contract">
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} md={3}>
                                <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} fullWidth value={values.rentalContract.startDate} onChange={(e) => setNested("rentalContract", "startDate", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField type="date" label="End Date" InputLabelProps={{ shrink: true }} fullWidth value={values.rentalContract.endDate} onChange={(e) => setNested("rentalContract", "endDate", e.target.value)} />
                            </Grid>
                        </Grid>
                        <FileUpload
                            label="Replace Rental File (PDF/Images only)"
                            accept=".pdf,.jpg,.jpeg,.png"
                            valueFile={values.rentalContract.file}
                            onPickFile={(f) => setNested("rentalContract", "file", f)}
                        />
                    </Section>

                    <Section title="Fire Safety Contract">
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} md={3}>
                                <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} fullWidth value={values.fireSafetyContract.startDate} onChange={(e) => setNested("fireSafetyContract", "startDate", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField type="date" label="End Date" InputLabelProps={{ shrink: true }} fullWidth value={values.fireSafetyContract.endDate} onChange={(e) => setNested("fireSafetyContract", "endDate", e.target.value)} />
                            </Grid>
                        </Grid>
                        <FileUpload
                            label="Replace Fire Safety File (PDF/Images only)"
                            accept=".pdf,.jpg,.jpeg,.png"
                            valueFile={values.fireSafetyContract.file}
                            onPickFile={(f) => setNested("fireSafetyContract", "file", f)}
                        />
                    </Section>

                    <Section title="Health License">
                        <Grid container spacing={2} mb={2}>
                            <Grid item xs={12} md={3}>
                                <TextField type="date" label="Issue Date" InputLabelProps={{ shrink: true }} fullWidth value={values.healthLicense.issueDate} onChange={(e) => setNested("healthLicense", "issueDate", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField type="date" label="Expiry Date" InputLabelProps={{ shrink: true }} fullWidth value={values.healthLicense.expiryDate} onChange={(e) => setNested("healthLicense", "expiryDate", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField label="Authority" fullWidth value={values.healthLicense.authority} onChange={(e) => setNested("healthLicense", "authority", e.target.value)} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField label="License Number" fullWidth value={values.healthLicense.licenseNumber} onChange={(e) => setNested("healthLicense", "licenseNumber", e.target.value)} />
                            </Grid>
                        </Grid>

                        <FileUpload
                            label="Replace License File (PDF/Images only)"
                            accept=".pdf,.jpg,.jpeg,.png"
                            valueFile={values.healthLicense.file}
                            onPickFile={(f) => setNested("healthLicense", "file", f)}
                        />
                    </Section>

                    <Section title="Notes">
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={values.notes}
                            onChange={(e) => setField("notes", e.target.value)}
                        />
                    </Section>
                </>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3500}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert
                    severity={snack.severity}
                    variant="filled"
                    onClose={() => setSnack({ ...snack, open: false })}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
